// My Trading Journey → Who I'm becoming (/app/journey/identity): the trader they want to be, next to
// what their trades say. The vision (from Goals) and up to five "I am a trader who…" statements,
// the habits to drop and to adopt, and a self-assessment checked against the data. The numbers
// come from identity.js; the statements and the self-assessment are saved at /api/identity.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { IDENTITY_MIN_TRADES, RATINGS, STATEMENTS, compare, identityReport, suggestions } from './identity';
import { formatMoney } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const dayText = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'long', day: 'numeric' });
const RATING_TEXT = { strength: 'Strength', ok: 'Average', weakness: 'Weakness' };
const COMPARE_TEXT = {
  agree: ['agree', 'Your trades agree.'],
  close: ['close', 'Close to what your trades say.'],
  blind: ['blind', 'Blind spot: you rate it a strength, your trades say it’s a weakness.'],
  hidden: ['hidden', 'Hidden strength: your trades say it’s better than you think.'],
};

function Statements({ identity, report, onSave }) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');
  const list = identity.statements;
  const add = (s) => list.length < 5 && onSave({ ...identity, statements: [...list, s] });
  const remove = (i) => onSave({ ...identity, statements: list.filter((_, j) => j !== i) });
  const checks = report && report.enough ? report.checks : list.map((s) => ({ ...s, held: null }));
  const ideas = suggestions(report, list);
  return (
    <section className="jo-card">
      <h2>{t('I am a trader who…')}</h2>
      <p className="field-hint">{t('Up to five statements about the trader you’re becoming. The ones that come from your habits are checked against your trades over the last 30 days.')}</p>
      {checks.length > 0 && (
        <ul className="ji-statements">
          {checks.map((s, i) => (
            <li key={`${s.text}-${i}`}>
              <span><b>{t('I am a trader who')}</b> {s.kind ? t(STATEMENTS[s.kind]) : s.text}</span>
              {s.held !== null && s.total > 0 && (
                <span className={`ji-held ${s.held === s.total ? 'gain' : s.held / s.total >= 0.8 ? '' : 'loss'}`}>
                  {t('Held on {n} of {total} trading days', { n: s.held, total: s.total })}
                </span>
              )}
              <button type="button" className="link-button" onClick={() => remove(i)} aria-label={t('Remove')}>{t('Remove')}</button>
            </li>
          ))}
        </ul>
      )}
      {list.length < 5 && (
        <>
          {ideas.length > 0 && (
            <div className="ji-ideas">
              <span className="field-hint">{t('From your habits:')}</span>
              {ideas.map((s) => <button key={s.kind} type="button" className="secondary small" onClick={() => add(s)}>+ {t('I am a trader who')} {t(s.text)}</button>)}
            </div>
          )}
          <form className="ji-add" onSubmit={(e) => { e.preventDefault(); if (draft.trim()) { add({ text: draft.trim() }); setDraft(''); } }}>
            <span>{t('I am a trader who')}</span>
            <input value={draft} maxLength={200} onChange={(e) => setDraft(e.target.value)} placeholder={t('e.g. reviews every trade the same day')} aria-label={t('Your statement')} />
            <button type="submit" className="small" disabled={!draft.trim()}>{t('Add')}</button>
          </form>
        </>
      )}
    </section>
  );
}

function Habits({ report }) {
  const { t } = useI18n();
  return (
    <div className="ji-two">
      <section className="jo-card">
        <h2>{t('Habits to drop')}</h2>
        <p className="field-hint">{t('What your journal caught in the last 60 days, costliest first.')}</p>
        {report.drop.length ? (
          <ul className="ji-habits">
            {report.drop.map((h) => (
              <li key={h.kind}>
                <div className="ji-habit-head">
                  <b>{t(h.name)}</b>
                  {h.cost !== null && <span className="loss">{formatMoney(h.cost)}</span>}
                </div>
                <span className="field-hint">
                  {t(h.count === 1 ? '1 trade' : '{n} trades', { n: h.count })} · {t(h.days === 1 ? '1 day' : '{n} days', { n: h.days })} · {t('last on {day}', { day: dayText(h.last) })}
                </span>
                <span className={`ji-clean${h.clean >= 5 ? ' gain' : ''}`}>{h.clean ? t(h.clean === 1 ? '1 clean trading day since' : '{n} clean trading days since', { n: h.clean }) : t('It happened on your last trading day')}</span>
                <span className="ji-rule">{h.rule}</span>
              </li>
            ))}
          </ul>
        ) : <p className="empty">{t('No costly habits in the last 60 days. Keep it that way.')}</p>}
      </section>
      <section className="jo-card">
        <h2>{t('Habits to adopt')}</h2>
        <p className="field-hint">{t('Your rules, and how often you kept each one over the last 30 days (days you checked your rules).')}</p>
        {report.adopt.length ? (
          <ul className="ji-habits">
            {report.adopt.map((h) => (
              <li key={h.rule}>
                <div className="ji-habit-head"><b>{h.rule}</b>{h.checked > 0 && <span className={h.kept === h.checked ? 'gain' : h.kept / h.checked < 0.6 ? 'loss' : ''}>{Math.round((h.kept / h.checked) * 100)}%</span>}</div>
                <span className="field-hint">{h.checked ? t('Kept on {n} of {total} days', { n: h.kept, total: h.checked }) : t('Not checked yet. Tick your rules in the End-of-day reflection.')}</span>
              </li>
            ))}
          </ul>
        ) : <p className="empty">{t('No rules yet.')} <Link to="/app/morning">{t('Write your rules')} →</Link></p>}
      </section>
    </div>
  );
}

function SelfCheck({ identity, report, onSave }) {
  const { t } = useI18n();
  const set = (id, rating) => onSave({ ...identity, self: { ...identity.self, [id]: rating } });
  return (
    <section className="jo-card">
      <h2>{t('How you see yourself, and what your trades say')}</h2>
      <p className="field-hint">{t('Rate each one honestly. Your trades from the last 60 days give their own answer next to yours.')}</p>
      <ul className="ji-traits">
        {report.traits.map((trait) => {
          const self = identity.self[trait.id];
          const c = compare(self, trait.data);
          return (
            <li key={trait.id}>
              <b>{t(trait.label)}</b>
              <div className="trade-chart-units" role="group" aria-label={t(trait.label)}>
                {RATINGS.map((r) => <button key={r} type="button" className={self === r ? 'active' : ''} aria-pressed={self === r} onClick={() => set(trait.id, r)}>{t(RATING_TEXT[r])}</button>)}
              </div>
              <span className={`ji-data ${trait.data || ''}`}>
                {trait.data ? `${t('Your trades:')} ${t(RATING_TEXT[trait.data])}` : t('Not enough data yet')}
                {trait.data && <span className="field-hint"> · {evidence(trait, t)}</span>}
              </span>
              {c && <span className={`ji-compare ${COMPARE_TEXT[c][0]}`}>{t(COMPARE_TEXT[c][1])}</span>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function evidence(trait, t) {
  if (trait.measure === 'days') return t('mistakes on {n} of {total} trading days', { n: trait.n, total: trait.total });
  if (trait.measure === 'winners') return t('{n} of {total} winners closed early', { n: trait.n, total: trait.total });
  if (trait.measure === 'bigLosses') return t('{n} of {total} losses were over twice your typical loss', { n: trait.n, total: trait.total });
  return t('all rules kept on {n} of {total} days you checked them', { n: trait.n, total: trait.total });
}

export default function JourneyIdentity({ accounts, trades, notes = {} }) {
  const { t, lang } = useI18n();
  const [identity, setIdentity] = useState(null);
  const [vision, setVision] = useState('');
  const [rules, setRules] = useState([]);
  const [error, setError] = useState('');
  useEffect(() => {
    api('/identity').then(setIdentity).catch(() => setIdentity({ statements: [], self: {} }));
    api('/goals').then((g) => setVision(g.vision || '')).catch(() => {});
    api('/routine').then((r) => setRules(r.rules || [])).catch(() => {});
  }, []);
  // `lang`: the suggested rules are written in the trader's language when the report is made.
  const report = useMemo(() => (identity ? identityReport(trades, { accounts, notes, rules, statements: identity.statements }) : null), [trades, accounts, notes, rules, identity, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save(next) {
    const before = identity;
    setIdentity(next);
    setError('');
    try { setIdentity(await api('/identity', { method: 'PUT', body: next })); } catch (err) { setIdentity(before); setError(err.message); }
  }

  if (!identity) return <p className="empty">{t('Loading…')}</p>;
  return (
    <div className="jo ji">
      <section className="jo-card ji-vision">
        <h2>{t('Who I’m becoming')}</h2>
        {vision ? <p className="ji-vision-text">“{vision}”</p> : <p className="field-hint">{t('You haven’t written your vision yet: the trader you want to be in a year.')}</p>}
        <Link to="/app/goals" className="bt-link-btn">{vision ? t('Edit in Goals') : t('Write it in Goals')} →</Link>
      </section>
      {error && <p className="error">{error}</p>}
      <Statements identity={identity} report={report} onSave={save} />
      {report && report.enough ? (
        <>
          <Habits report={report} />
          <SelfCheck identity={identity} report={report} onSave={save} />
        </>
      ) : (
        <p className="empty">{t('Your habits and the check against your trades need at least {min} trades in the last 60 days. You have {n}.', { min: IDENTITY_MIN_TRADES, n: report ? report.count : 0 })}</p>
      )}
    </div>
  );
}
